import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Checkbox, SpinnerWithBackground, Text } from '@viapeople/components'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import { useLoopStore } from '../store'

/**
 * @fileoverview Loop Launch Tab
 *
 * Launch loop executions and manage active executions
 */

export const LoopLaunchTab = () => {
	const { loopId } = useParams<{ loopId: string }>()
	const { user } = sessionStore()
	const organizationId = user?.organizationId
	const { loop, useMarkReadyLoop } = useLoopStore()
	const { markReadyLoop, isLoading: isMarkingReady } = useMarkReadyLoop(loopId as string)
	const toast = useToast()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [isLaunching, setIsLaunching] = useState(false)
	const [isClosing, setIsClosing] = useState(false)
	const [sendEmail, setSendEmail] = useState(true)

	// Fetch loop statistics
	const { data: statistics } = useQuery({
		...trpc.client.loops.getStatistics.queryOptions({
			id: loopId as string,
			organizationId: organizationId as string,
		}),
		enabled: !!loopId && !!organizationId,
	})

	// Fetch active execution
	const { data: activeExecution, isLoading: isLoadingExecution } = useQuery({
		...trpc.client.loops.getActiveExecution.queryOptions({
			loopId: loopId as string,
		}),
		enabled: !!loopId,
	})

	const launchMutation = useMutation({
		...trpc.client.loops.launch.mutationOptions(),
		onSuccess: (data) => {
			toast.showToast(`Loop execution launched successfully with ${data.participantCount} participants`)
			setIsLaunching(false)
			// Invalidate to refetch active execution
			queryClient.invalidateQueries({
				queryKey: trpc.client.loops.getActiveExecution.queryKey(),
			})
			queryClient.invalidateQueries({
				queryKey: trpc.client.loops.getById.queryKey(),
			})
		},
		onError: (error) => {
			toast.showToast(error.message || 'Failed to launch execution', 'error')
			setIsLaunching(false)
		},
	})

	const closeExecutionMutation = useMutation({
		...trpc.client.loops.closeExecution.mutationOptions(),
		onSuccess: () => {
			toast.showToast('Loop execution closed successfully')
			setIsClosing(false)
			// Invalidate to refetch active execution
			queryClient.invalidateQueries({
				queryKey: trpc.client.loops.getActiveExecution.queryKey(),
			})
			queryClient.invalidateQueries({
				queryKey: trpc.client.loops.getById.queryKey(),
			})
		},
		onError: (error) => {
			toast.showToast(error.message || 'Failed to close execution', 'error')
			setIsClosing(false)
		},
	})

	const handleLaunch = async () => {
		if (!loop || !organizationId || !loopId) return

		if (loop.status !== 'ready' && loop.status !== 'active') {
			toast.showToast('Loop must be marked as ready before launching', 'error')
			return
		}

		if (!statistics?.participantsCount || statistics.participantsCount === 0) {
			toast.showToast('Please add participants first', 'error')
			return
		}

		if (sendEmail && !loop.emailTemplateId) {
			toast.showToast('Please select an email template first', 'error')
			return
		}

		setIsLaunching(true)
		await launchMutation.mutateAsync({
			loopId,
			organizationId,
			...(sendEmail && loop.emailTemplateId ? { emailTemplateId: loop.emailTemplateId } : {}),
		})
	}

	const handleCloseExecution = async () => {
		if (!activeExecution || !organizationId) return

		setIsClosing(true)
		await closeExecutionMutation.mutateAsync({
			executionId: activeExecution.id,
			organizationId,
		})
	}

	const handleViewResults = () => {
		if (!organizationId || !loopId || !activeExecution) return
		navigate(`/loops/${loopId}/results/execution/${activeExecution.id}`)
	}

	const handleMarkReady = async () => {
		if (!loop || !organizationId || !loopId) return

		if (!statistics?.participantsCount || statistics.participantsCount === 0) {
			toast.showToast('Please add participants first', 'error')
			return
		}

		if (!loop.textQuestionEnabled && !loop.ratingQuestionEnabled) {
			toast.showToast('Please enable at least one question type', 'error')
			return
		}

		if (loop.textQuestionEnabled && !loop.textQuestionLabel) {
			toast.showToast('Please configure text question label', 'error')
			return
		}

		if (loop.ratingQuestionEnabled && (!loop.ratingQuestionLabel || !loop.ratingScale)) {
			toast.showToast('Please configure rating question label and scale', 'error')
			return
		}

		await markReadyLoop()
		queryClient.invalidateQueries({
			queryKey: trpc.client.loops.getById.queryKey({ id: loopId }),
		})
	}

	const isPending = isLaunching || isClosing || isLoadingExecution || isMarkingReady

	// Check readiness
	const isReady = loop?.status === 'ready' || loop?.status === 'active'
	const hasParticipants = statistics && statistics.participantsCount > 0
	const hasQuestions =
		(loop?.textQuestionEnabled && loop?.textQuestionLabel) ||
		(loop?.ratingQuestionEnabled && loop?.ratingQuestionLabel && loop?.ratingScale)
	const hasEmailTemplate = !!loop?.emailTemplateId

	const canLaunch = isReady && hasParticipants && hasQuestions && !activeExecution && (!sendEmail || hasEmailTemplate)

	return (
		<div className='relative pb-12 mt-6 w-full'>
			{isPending && <SpinnerWithBackground />}
			<div className='px-6 max-w-4xl space-y-6'>
				{/* Loop Status */}
				<div>
					<Text variant='large' weight='semibold'>
						Loop Status
					</Text>
					<div className='mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4'>
						<div className='grid grid-cols-2 gap-4'>
							<div>
								<Text variant='xsmall' className='text-gray-600'>
									Status
								</Text>
								<Text variant='small' weight='semibold' className='mt-1'>
									{loop?.status.toUpperCase()}
								</Text>
							</div>
							<div>
								<Text variant='xsmall' className='text-gray-600'>
									Participants
								</Text>
								<Text variant='small' weight='semibold' className='mt-1'>
									{statistics?.participantsCount || 0}
								</Text>
							</div>
							<div>
								<Text variant='xsmall' className='text-gray-600'>
									Total Executions
								</Text>
								<Text variant='small' weight='semibold' className='mt-1'>
									{statistics?.executionsCount || 0}
								</Text>
							</div>
							<div>
								<Text variant='xsmall' className='text-gray-600'>
									Active Execution
								</Text>
								<Text variant='small' weight='semibold' className='mt-1'>
									{activeExecution ? 'Yes' : 'No'}
								</Text>
							</div>
						</div>
					</div>
				</div>

				{/* Pre-Launch Checklist */}
				<div>
					<Text variant='medium' weight='semibold'>
						Pre-Launch Checklist
					</Text>
					<div className='mt-4 space-y-2'>
						<div className='flex items-center gap-2'>
							<div
								className={`w-5 h-5 rounded-full flex items-center justify-center ${
									isReady ? 'bg-green-500' : 'bg-gray-300'
								}`}>
								{isReady && (
									<svg
										className='w-3 h-3 text-white'
										fill='none'
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth='2'
										viewBox='0 0 24 24'
										stroke='currentColor'
										aria-hidden='true'>
										<path d='M5 13l4 4L19 7'></path>
									</svg>
								)}
							</div>
							<Text variant='small' className={isReady ? 'text-gray-900' : 'text-gray-500'}>
								Loop is marked as ready
							</Text>
						</div>
						<div className='flex items-center gap-2'>
							<div
								className={`w-5 h-5 rounded-full flex items-center justify-center ${
									hasParticipants ? 'bg-green-500' : 'bg-gray-300'
								}`}>
								{hasParticipants && (
									<svg
										className='w-3 h-3 text-white'
										fill='none'
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth='2'
										viewBox='0 0 24 24'
										stroke='currentColor'
										aria-hidden='true'>
										<path d='M5 13l4 4L19 7'></path>
									</svg>
								)}
							</div>
							<Text variant='small' className={hasParticipants ? 'text-gray-900' : 'text-gray-500'}>
								Participants added ({statistics?.participantsCount || 0})
							</Text>
						</div>
						<div className='flex items-center gap-2'>
							<div
								className={`w-5 h-5 rounded-full flex items-center justify-center ${
									hasQuestions ? 'bg-green-500' : 'bg-gray-300'
								}`}>
								{hasQuestions && (
									<svg
										className='w-3 h-3 text-white'
										fill='none'
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth='2'
										viewBox='0 0 24 24'
										stroke='currentColor'
										aria-hidden='true'>
										<path d='M5 13l4 4L19 7'></path>
									</svg>
								)}
							</div>
							<Text variant='small' className={hasQuestions ? 'text-gray-900' : 'text-gray-500'}>
								Questions configured
							</Text>
						</div>
						<div className='flex items-center gap-2'>
							<div
								className={`w-5 h-5 rounded-full flex items-center justify-center ${
									!sendEmail || hasEmailTemplate ? 'bg-green-500' : 'bg-gray-300'
								}`}>
								{(!sendEmail || hasEmailTemplate) && (
									<svg
										className='w-3 h-3 text-white'
										fill='none'
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth='2'
										viewBox='0 0 24 24'
										stroke='currentColor'
										aria-hidden='true'>
										<path d='M5 13l4 4L19 7'></path>
									</svg>
								)}
							</div>
							<Text variant='small' className={!sendEmail || hasEmailTemplate ? 'text-gray-900' : 'text-gray-500'}>
								{sendEmail ? 'Email template selected' : 'Email disabled (skipped)'}
							</Text>
						</div>
					</div>
				</div>

				{/* Active Execution Section */}
				{activeExecution && (
					<div>
						<Text variant='medium' weight='semibold'>
							Active Execution
						</Text>
						<div className='mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4'>
							<div className='grid grid-cols-2 gap-4'>
								<div>
									<Text variant='xsmall' className='text-gray-600'>
										Launched At
									</Text>
									<Text variant='small' weight='semibold' className='mt-1'>
										{new Date(activeExecution.launchedAt).toLocaleString()}
									</Text>
								</div>
								<div>
									<Text variant='xsmall' className='text-gray-600'>
										Status
									</Text>
									<Text variant='small' weight='semibold' className='mt-1'>
										{activeExecution.status.toUpperCase()}
									</Text>
								</div>
								<div>
									<Text variant='xsmall' className='text-gray-600'>
										Total Responses
									</Text>
									<Text variant='small' weight='semibold' className='mt-1'>
										{statistics?.participantsCount || 0}
									</Text>
								</div>
								<div>
									<Text variant='xsmall' className='text-gray-600'>
										Completed Responses
									</Text>
									<Text variant='small' weight='semibold' className='mt-1'>
										{/* Will need to add this to active execution query */}-
									</Text>
								</div>
							</div>
							<div className='flex gap-2 mt-4'>
								<Button variant='primary' onClick={handleViewResults}>
									View Results
								</Button>
								<Button variant='destructive' onClick={handleCloseExecution} disabled={isClosing}>
									{isClosing ? 'Closing...' : 'Close Execution'}
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Mark as Ready Section */}
				{loop?.status === 'draft' && (
					<div>
						<Text variant='medium' weight='semibold'>
							Mark as Ready
						</Text>
						<div className='mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
							<Text variant='small' className='text-gray-700 mb-4'>
								Before you can launch this loop, you need to mark it as ready. This confirms that all configurations are
								complete.
							</Text>
							<div className='space-y-2 mb-4'>
								<div className='flex items-center gap-2'>
									<div
										className={`w-4 h-4 rounded-full flex items-center justify-center ${
											hasParticipants ? 'bg-green-500' : 'bg-gray-300'
										}`}>
										{hasParticipants && (
											<svg
												className='w-2.5 h-2.5 text-white'
												fill='none'
												strokeLinecap='round'
												strokeLinejoin='round'
												strokeWidth='2'
												viewBox='0 0 24 24'
												stroke='currentColor'
												aria-hidden='true'>
												<path d='M5 13l4 4L19 7'></path>
											</svg>
										)}
									</div>
									<Text variant='small' className={hasParticipants ? 'text-gray-900' : 'text-gray-500'}>
										Population configured
									</Text>
								</div>
								<div className='flex items-center gap-2'>
									<div
										className={`w-4 h-4 rounded-full flex items-center justify-center ${
											hasQuestions ? 'bg-green-500' : 'bg-gray-300'
										}`}>
										{hasQuestions && (
											<svg
												className='w-2.5 h-2.5 text-white'
												fill='none'
												strokeLinecap='round'
												strokeLinejoin='round'
												strokeWidth='2'
												viewBox='0 0 24 24'
												stroke='currentColor'
												aria-hidden='true'>
												<path d='M5 13l4 4L19 7'></path>
											</svg>
										)}
									</div>
									<Text variant='small' className={hasQuestions ? 'text-gray-900' : 'text-gray-500'}>
										Questions configured
									</Text>
								</div>
							</div>
							<Button
								variant='primary'
								onClick={handleMarkReady}
								disabled={!hasParticipants || !hasQuestions || isMarkingReady}>
								{isMarkingReady ? 'Marking as Ready...' : 'Mark as Ready'}
							</Button>
						</div>
					</div>
				)}

				{/* Launch Section */}
				{!activeExecution && (
					<div>
						<Text variant='medium' weight='semibold'>
							Launch Execution
						</Text>
						<div className='mt-4 space-y-4'>
							<div className='bg-white border border-gray-200 rounded-lg p-4 space-y-4'>
								<div>
									<Checkbox
										label='Send email notifications'
										checked={sendEmail}
										onChange={(e) => setSendEmail(e.target.checked)}
									/>
									<Text variant='xsmall' className='text-gray-500 mt-1'>
										{sendEmail
											? 'Participants will receive an email invitation'
											: 'Participants will not receive email notifications'}
									</Text>
								</div>

								{sendEmail && !hasEmailTemplate && (
									<div className='bg-yellow-50 border border-yellow-200 rounded-lg p-3'>
										<Text variant='small' className='text-yellow-800'>
											Please configure an email template in the Email Template tab before launching.
										</Text>
									</div>
								)}

								<div>
									<Text variant='small' weight='semibold' className='mb-2'>
										Summary
									</Text>
									<div className='space-y-1'>
										<Text variant='xsmall' className='text-gray-600'>
											• {statistics?.participantsCount || 0} participants will be invited
										</Text>
										{loop?.textQuestionEnabled && (
											<Text variant='xsmall' className='text-gray-600'>
												• Text question: "{loop.textQuestionLabel}"
											</Text>
										)}
										{loop?.ratingQuestionEnabled && (
											<Text variant='xsmall' className='text-gray-600'>
												• Rating question: "{loop.ratingQuestionLabel}" (0-{loop.ratingScale})
											</Text>
										)}
										{sendEmail && hasEmailTemplate && (
											<Text variant='xsmall' className='text-gray-600'>
												• Email notifications will be sent
											</Text>
										)}
									</div>
								</div>

								<Button
									variant='primary'
									onClick={handleLaunch}
									disabled={!canLaunch || isLaunching}
									className='w-full'>
									{isLaunching ? 'Launching...' : 'Launch Execution'}
								</Button>

								{!canLaunch && !activeExecution && (
									<Text variant='xsmall' className='text-red-600'>
										{!isReady && '• Loop must be marked as ready'}
										{!hasParticipants && '• Add participants first'}
										{!hasQuestions && '• Configure questions first'}
										{sendEmail && !hasEmailTemplate && '• Select an email template'}
									</Text>
								)}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
